// BUILD-046 increment 1a: the "Assigned / age" column of Assigned by Me - when a request was ASSIGNED and how old it is.
//
// The date is the request's ORIGINAL assignment timestamp (`createdAt` of the entry GET /work/issued returns, which is the request row's creation instant). It is never a
// last-updated time. It is shown on the Phoenix calendar ("Oct 19": the company's day, not the browser's zone) with a quiet age counted in Phoenix CALENDAR DAYS against the
// page's current Phoenix day (usePhoenixDay), so it moves at 00:00 Phoenix and not 24 hours after the request:
//
//   0 days            Today
//   1 day             1 day
//   2 to 6 days       N days
//   7 to 29 days      N weeks   (whole weeks: 7 to 13 days is 1 week ... 28 and 29 days is 4 weeks)
//   30 to 364 days    N months  (whole 30-day months)
//   365 days or more  N years   (whole 365-day years)
//
// The age is INFORMATIONAL. This module knows nothing about "overdue"; the age is never red and never reaches the one overdue rule (overdueRule.ts).
//
// A timestamp that is missing, empty, "-" (the server's placeholder for a pointer with no time), not an ISO instant WITH a zone designator, or not a real instant is NOT
// AVAILABLE: the date and the age read "Not available" - never invented, never "0 days". An assignment time in the future of the page's day (a device clock behind the server)
// reads "Today": the age is never negative.
//
// Pure module. Runtime imports: the Phoenix-day helpers of dateLabels.ts and the short date of dueLabel.ts (both pure). Erasable TypeScript only.

import { isIsoDate, phoenixToday } from './dateLabels';
import { shortDate } from './dueLabel';

/** The word shown wherever the server did not supply a value. */
export const NOT_AVAILABLE = 'Not available';

// An ISO instant WITH a zone designator ("Z" or an offset): without one `Date.parse` would read the browser's zone, and the Phoenix day would depend on the device.
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

/** The Phoenix calendar day (YYYY-MM-DD) of an ISO instant, or null when it is not a usable instant. */
export function assignedPhoenixDay(createdAt: unknown): string | null {
  if (typeof createdAt !== 'string' || !INSTANT.test(createdAt)) return null;
  const ms = Date.parse(createdAt);
  if (!Number.isFinite(ms)) return null;
  const day = phoenixToday(new Date(ms));
  return isIsoDate(day) ? day : null;
}

/** A calendar day as a whole number of days (UTC arithmetic on the parts, so no zone or daylight-saving change can shift it). */
function dayNumber(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number);
  return Math.round(Date.UTC(year, month - 1, day) / 86400000);
}

/** Whole Phoenix calendar days from `assignedDay` to `todayIso`, never negative. Both must be real YYYY-MM-DD days (else 0). */
export function ageInDays(assignedDay: unknown, todayIso: unknown): number {
  if (!isIsoDate(assignedDay) || !isIsoDate(todayIso)) return 0;
  return Math.max(0, dayNumber(todayIso) - dayNumber(assignedDay));
}

const plural = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** The quiet age words for a number of days (see the table above). A negative or non-finite number reads as 0. */
export function ageWords(days: number): string {
  const whole = Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
  if (whole === 0) return 'Today';
  if (whole < 7) return plural(whole, 'day');
  if (whole < 30) return plural(Math.floor(whole / 7), 'week');
  if (whole < 365) return plural(Math.floor(whole / 30), 'month');
  return plural(Math.floor(whole / 365), 'year');
}

export interface AssignedAge {
  /** False when the timestamp was missing or unusable: `dateText` is then "Not available" and `ageText` is ''. */
  available: boolean;
  /** The Phoenix day the request was assigned (YYYY-MM-DD), or null. */
  day: string | null;
  /** "Oct 19" (with the year when it is not the current year), or "Not available". */
  dateText: string;
  /** "Today", "1 day", "5 days", "3 weeks", "2 months", or '' when not available. */
  ageText: string;
  /** Whole Phoenix days since the assignment (never negative), or null when not available. */
  days: number | null;
  /** The tooltip: the full date and the age in words, or why there is none. */
  title: string;
}

/** The "Assigned / age" cell for one request: `createdAt` is the request's original assignment instant, `todayIso` the page's current Phoenix day. */
export function describeAssignedAge(createdAt: unknown, todayIso: unknown): AssignedAge {
  const day = assignedPhoenixDay(createdAt);
  if (day === null) return { available: false, day: null, dateText: NOT_AVAILABLE, ageText: '', days: null, title: 'The date this was assigned was not supplied by the server' };
  const today = isIsoDate(todayIso) ? todayIso : phoenixToday();
  const days = ageInDays(day, today);
  const ageText = ageWords(days);
  const [year, month, date] = day.split('-');
  const full = `${month}/${date}/${year}`;
  return { available: true, day, dateText: shortDate(day, today), ageText, days, title: `Assigned ${full} (Phoenix date), ${days === 0 ? 'today' : `${ageText} ago`}` };
}

/** The assignment instant in milliseconds for ordering, or null when it is not usable (missing values are last in both directions). */
export function assignedInstant(createdAt: unknown): number | null {
  if (assignedPhoenixDay(createdAt) === null) return null;
  return Date.parse(createdAt as string);
}
